import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowRight, MailCheck, MailWarning } from "lucide-react";
import { Section, Overline, H1, H2 } from "@/components/ui-kit";
import { EmailInput } from "@/components/forms/fields";
import { emailError } from "@/lib/format";

const COPY = {
  seller: "Thank you. We received your property information. Someone from Brightside Capital Ventures will contact you shortly.",
  investor: "Thank you. We received your investment interest. Someone from Brightside Capital Ventures will contact you shortly to discuss your goals.",
  buyer: "Thank you. We received your buyer profile. Someone from Brightside Capital Ventures will contact you shortly.",
};

export function VerificationPanel({ leadId, initial }) {
  const [status, setStatus] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [changing, setChanging] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");

  const refresh = async () => {
    try { const r = await axios.get(`/api/leads/${leadId}/status`); setStatus(r.data); } catch { /* keep last */ }
  };
  useEffect(() => { if (leadId) refresh(); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn) => {
    setBusy(true); setMsg("");
    try { const r = await fn(); setStatus((s) => ({ ...s, ...r.data })); setMsg(r.data.sent ? "Email sent. Please check your inbox and spam folder." : "We could not send the email right now. We still have your submission and will contact you directly."); setChanging(false); }
    catch (e) { setMsg(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong. Please try again in a minute."); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  const verified = status.email_verification_status === "verified";
  return (
    <div className="mt-8 card-frame p-6 md:p-8" data-testid="verification-panel">
      <div className="flex items-start gap-3">
        {verified ? <MailCheck className="text-emerald-400 shrink-0" size={22} /> : <MailWarning className="text-[#C19B6C] shrink-0" size={22} />}
        <div className="flex-1">
          <p className="text-sm font-semibold text-white" data-testid="verification-status">{verified ? "Email confirmed" : "Received. Email confirmation pending."}</p>
          {!verified && (
            <p className="text-sm text-neutral-300 mt-1">
              {status.confirmation_email_status === "sent"
                ? <>Please check your email to confirm your address. We sent a confirmation link to <span className="text-white">{status.email_masked}</span>.</>
                : <>Please check your email to confirm your address. {status.confirmation_email_error}</>}
            </p>
          )}
          {status.lead_type === "buyer" && status.property_updates_opt_in && (
            <p className="text-xs text-neutral-400 mt-3" data-testid="updates-status">
              {status.property_updates_enrolled ? "You are on the property-update list." : verified ? "Your property-update preference is saved. We will add you to the list when it goes live." : "Property updates start after you confirm your email address."}
            </p>
          )}
          {!verified && (
            <div className="mt-4 flex flex-wrap gap-3">
              <button type="button" disabled={busy || status.resend_count >= 3} onClick={() => act(() => axios.post(`/api/leads/${leadId}/resend-verification`))} className="btn-ghost px-4 py-2 text-xs disabled:opacity-50" data-testid="resend-verification">{busy ? "Sending…" : "Resend confirmation email"}</button>
              <button type="button" onClick={() => setChanging((c) => !c)} className="btn-ghost px-4 py-2 text-xs" data-testid="change-email-toggle">Use a different email</button>
            </div>
          )}
          {changing && !verified && (
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <div className="flex-1"><EmailInput value={newEmail} onChange={setNewEmail} testId="change-email-input" /></div>
              <button type="button" disabled={busy} onClick={() => { const e = emailError(newEmail); setEmailErr(e); if (!e) act(() => axios.post(`/api/leads/${leadId}/change-email`, { email: newEmail })); }} className="btn-gold px-5 py-3 text-sm font-semibold disabled:opacity-50" data-testid="change-email-submit">Update and resend</button>
            </div>
          )}
          {emailErr && <p className="text-xs text-red-400 mt-2">{emailErr}</p>}
          {msg && <p className="text-xs text-neutral-300 mt-3" role="status" data-testid="verification-message">{msg}</p>}
        </div>
      </div>
    </div>
  );
}

export default function ThankYou() {
  const location = useLocation();
  const lead = location.state?.lead || {};
  const kind = lead.lead_type || location.state?.kind || "";
  const isSeller = kind === "seller";

  return (
    <Section className="pt-20 md:pt-28 pb-24" dataTestId="thank-you-page">
      <div className="max-w-3xl">
        <CheckCircle2 className="text-[#C19B6C]" size={36} strokeWidth={1.4} />
        <Overline className="mt-5">Submission received</Overline>
        <H1 className="mt-3">Thanks. Your information was received.</H1>
        <p className="text-neutral-300 mt-6 leading-relaxed" data-testid="thank-you-copy">
          {COPY[kind] || "Thank you. We received your information and will follow up shortly."}
        </p>
        {lead.id && ["seller", "investor", "buyer"].includes(kind) && <VerificationPanel leadId={lead.id} initial={{ ...lead, confirmation_email_status: "pending" }} />}

        {isSeller && (
          <div className="mt-12 card-frame p-7 md:p-10">
            <H2 className="text-xl">What happens next</H2>
            <ol className="mt-6 space-y-4 text-sm text-neutral-300">
              {["We review the property information.", "We look at condition, location, timeline, and possible deal structures.", "We contact you to understand the situation better.", "If there is a fit, we discuss practical next steps."].map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex-shrink-0 w-6 h-6 border border-[#C19B6C] text-[#C19B6C] flex items-center justify-center text-xs font-semibold">{i + 1}</span>
                  <span className="leading-relaxed pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
            {lead.lead_priority === "Hot" && <p className="text-xs text-[#C19B6C] mt-6">Your situation looks time-sensitive. Expect outreach within one business day.</p>}
          </div>
        )}

        <div className="mt-10 card-frame p-7 md:p-8 border-l-4 border-l-[#C19B6C]">
          <p className="text-sm text-neutral-200 leading-relaxed"><strong>While we review, you can also call or text for a faster response.</strong></p>
          <a href="tel:+19166780058" className="btn-gold inline-flex items-center gap-2 mt-5 px-6 py-3.5 font-semibold text-base" data-testid="thankyou-call-cta">Call (916) 678-0058</a>
          <p className="text-xs text-neutral-500 mt-3 italic">Direct line, not a call center.</p>
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/" className="btn-ghost px-6 py-3 text-sm inline-flex items-center gap-2">Back to home <ArrowRight size={14} /></Link>
          <Link to="/deals" className="btn-ghost px-6 py-3 text-sm">View case studies</Link>
        </div>
      </div>
    </Section>
  );
}
